import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useChatContext } from '../contexts/ChatContext';
import api from '../utils/api';
import { trackGameEvent } from '../utils/gameEvents';

const SPRITE_BASE = 'https://play.pokemonshowdown.com/sprites/ani';
const SPRITE_HEIGHT = 56;

// Special-character emoji map — kept in sync with the backend special_characters seed.
// Used as a fallback when the roster doesn't supply character_emoji (e.g. own sprite).
// Mirrors the active rows of special_characters in the DB. Retired chars
// (Andy, Maradona, Spinetta, Charly, Messi) are filtered out via is_active=false
// in the DB; this map is just a render fallback.
const CHARACTER_EMOJI = {
  char_mj: '🕺', char_freddie: '🎤',
  char_einstein: '🧠', char_bowie: '🎸', char_picasso: '🎨', char_chaplin: '🎩',
  char_grohl: '🥁', char_lennon: '🎼',
  char_cerati: '🎙️',
  // Metal heroes (added for Gonzalo, who is a metal fan).
  char_kiss: '👅', char_megadeth: '💀', char_axl: '🌹',
  char_maiden: '🤘', char_metallica: '⚡',
  // Custom-art character — falls back to 🤘 if sprite_url unreachable.
  char_gonzalo: '🤘',
};
function isCharacterSlug(slug) { return typeof slug === 'string' && slug.startsWith('char_'); }
const WALK_SPEED = 35; // px/sec — base, varied per sprite
const OVERLAY_HEIGHT = SPRITE_HEIGHT + 56; // headroom for jumps + label
const BOB_AMPLITUDE = 2.5;     // px, Pokémon vertical bob (additive over GIF walk cycle)
const BOB_FREQ = 7;            // Hz, Pokémon bob — matches sprite GIF cadence
const CHAR_BOB_AMPLITUDE = 2.5; // px, emoji-character bob — kept low to avoid jitter
const CHAR_STEP_HZ = 1.9;      // Hz, ~2 steps/sec — natural human walk pace
const JUMP_HEIGHT = 22;        // px peak
const JUMP_DURATION = 600;     // ms full arc
const HOP_HEIGHT = 8;          // px peak (turnaround hop)
const HOP_DURATION = 320;      // ms
const ROSTER_POLL_MS = 60_000;

// One walking sprite. Self-contained RAF + imperative handle so the overlay
// can query position and pause/faint it during a battle.
const Sprite = forwardRef(function Sprite(
  { slug, label, onClick, showcase, badge, characterEmoji, spriteUrl },
  apiRef
) {
  // Render mode:
  //   - 'image' when we have a real sprite asset (Pokémon GIF or admin-set
  //     character sprite_url). Uses scaleX(facing) flip + minimal bob.
  //   - 'emoji' when falling back to an emoji glyph for a character slug.
  //     Uses subdued motion (no squash, no lean) so the glyph doesn't wobble.
  const renderMode = (!isCharacterSlug(slug) || spriteUrl) ? 'image' : 'emoji';
  const imageSrc = isCharacterSlug(slug) ? spriteUrl : `${SPRITE_BASE}/${slug}.gif`;
  const elRef = useRef(null);
  const stateRef = useRef(null);
  const labelRef = useRef(null);

  useImperativeHandle(apiRef, () => ({
    getPosition: () => stateRef.current ? { x: stateRef.current.x, facing: stateRef.current.facing } : null,
    isFainted: () => !!stateRef.current?.fainted,
    setPaused: (v) => { if (stateRef.current) stateRef.current.paused = !!v; },
    setFainted: (v) => { if (stateRef.current) stateRef.current.fainted = !!v; },
    triggerHop: () => {
      const s = stateRef.current;
      if (!s || s.fainted) return;
      const now = performance.now();
      // Bigger jump than the spontaneous one + a single 360° spin during it.
      s.jumpStart = now;
      s.jumpDur = 520;
      s.jumpPeak = JUMP_HEIGHT * 1.6;
      s.spinStart = now;
      s.spinDur = 520;
    },
    respawn: () => {
      if (!stateRef.current) return;
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
      const fromLeft = Math.random() < 0.5;
      stateRef.current.x = fromLeft ? 0 : Math.max(0, vw - 80);
      stateRef.current.facing = fromLeft ? 1 : -1;
    },
  }));

  useEffect(() => {
    const vw = () => window.innerWidth;
    const now0 = performance.now();
    stateRef.current = {
      x: Math.round(Math.random() * Math.max(1, vw() - 80)),
      facing: Math.random() < 0.5 ? -1 : 1,
      mode: 'walk',
      modeUntil: now0 + 2000 + Math.random() * 4000,
      speed: WALK_SPEED * (0.75 + Math.random() * 0.5), // ±25% per sprite
      bobPhase: Math.random() * Math.PI * 2,
      stepPhase: Math.random() * Math.PI * 2, // characters use a slower step phase
      currentLean: 0,                          // smoothed lean angle (eased toward target)
      jumpStart: 0,
      jumpDur: 0,
      jumpPeak: 0,
      nextJumpAt: now0 + 4000 + Math.random() * 9000, // first jump in 4-13s
      paused: false,
      fainted: false,
    };

    let raf = 0;
    let last = now0;

    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const s = stateRef.current;
      if (!s) return;

      // 'emoji' mode renders a flat glyph (no horizontal flip — emojis don't
      // have a coherent left/right orientation). 'image' mode flips with facing.
      const isEmojiOnly = renderMode === 'emoji';

      // If fainted: render upside-down, keep position. Skip motion + bobs + jumps.
      if (s.fainted) {
        const ix = Math.round(s.x);
        if (elRef.current) {
          elRef.current.style.transform = isEmojiOnly
            ? `translate(${ix}px, 0) rotate(180deg)`
            : `translate(${ix}px, 0) scaleX(${-s.facing}) rotate(180deg)`;
          elRef.current.style.opacity = '0.6';
        }
        if (labelRef.current) {
          labelRef.current.style.transform = `translate(${ix}px, 0)`;
        }
        raf = requestAnimationFrame(tick);
        return;
      }

      // Restore opacity in case we were fainted previously
      if (elRef.current && elRef.current.style.opacity) elRef.current.style.opacity = '';

      // If paused (mid-battle VS flash): freeze in place but don't apply faint visuals.
      if (s.paused) {
        const ix = Math.round(s.x);
        if (elRef.current) {
          elRef.current.style.transform = isEmojiOnly
            ? `translate(${ix}px, 0)`
            : `translate(${ix}px, 0) scaleX(${-s.facing})`;
        }
        if (labelRef.current) {
          labelRef.current.style.transform = `translate(${ix}px, 0)`;
        }
        raf = requestAnimationFrame(tick);
        return;
      }

      // Mode switch (walk ↔ idle)
      if (now > s.modeUntil) {
        if (s.mode === 'walk' && Math.random() < 0.35) {
          s.mode = 'idle';
          s.modeUntil = now + 1500 + Math.random() * 2500;
        } else {
          s.mode = 'walk';
          if (Math.random() < 0.25) s.facing *= -1;
          s.modeUntil = now + 3000 + Math.random() * 5000;
        }
      }

      // Horizontal motion + edge bounce (with hop)
      if (s.mode === 'walk' && !s.jumpStart) {
        s.x += s.facing * s.speed * dt;
        const max = vw() - 64;
        if (s.x < 0) {
          s.x = 0; s.facing = 1;
          s.jumpStart = now; s.jumpDur = HOP_DURATION; s.jumpPeak = HOP_HEIGHT;
        } else if (s.x > max) {
          s.x = max; s.facing = -1;
          s.jumpStart = now; s.jumpDur = HOP_DURATION; s.jumpPeak = HOP_HEIGHT;
        }
      }

      // Spontaneous jumps while walking
      if (s.mode === 'walk' && !s.jumpStart && now >= s.nextJumpAt) {
        s.jumpStart = now;
        s.jumpDur = JUMP_DURATION;
        s.jumpPeak = JUMP_HEIGHT * (0.85 + Math.random() * 0.3);
        s.nextJumpAt = now + 6000 + Math.random() * 12000;
      }

      // Walking bob — both image and emoji sprites get a small vertical bob.
      // Image sprites (Pokémon GIFs, character sprite_url images) have built-in
      // walk frames so we keep the bob minimal and skip squash/lean entirely.
      // Emoji-only sprites get a subtle bob too; we deliberately do NOT squash
      // or lean them because emoji glyphs wobble badly under per-frame scale.
      const bobActive = s.mode === 'walk' && !s.jumpStart;
      if (bobActive) {
        s.bobPhase += dt * BOB_FREQ * 2 * Math.PI;
        s.stepPhase += dt * CHAR_STEP_HZ * 2 * Math.PI;
      }

      let bobY;
      if (isEmojiOnly) {
        bobY = bobActive ? Math.abs(Math.sin(s.stepPhase)) * CHAR_BOB_AMPLITUDE : 0;
      } else {
        bobY = bobActive ? Math.abs(Math.sin(s.bobPhase)) * BOB_AMPLITUDE : 0;
      }

      // Jump arc — parabola y = 4h * t * (1 - t)
      let jumpY = 0;
      if (s.jumpStart) {
        const t = (now - s.jumpStart) / s.jumpDur;
        if (t >= 1) {
          s.jumpStart = 0;
        } else {
          jumpY = 4 * s.jumpPeak * t * (1 - t);
        }
      }

      const y = bobY + jumpY;

      // One-shot click spin — a 360° rotation that runs over spinDur ms.
      let spinDeg = 0;
      if (s.spinStart) {
        const t = (now - s.spinStart) / s.spinDur;
        if (t >= 1) {
          s.spinStart = 0;
        } else {
          spinDeg = 360 * t;
        }
      }

      // Round to integer pixels before applying. With imageRendering:pixelated
      // (Pokémon GIFs + admin sprite_url images), sub-pixel translate values
      // make the rasterizer snap inconsistently per frame and the sprite
      // visibly shimmers/jitters. Round once and emoji + img both stay still.
      const ix = Math.round(s.x);
      const iy = Math.round(y);
      const iSpin = Math.round(spinDeg);
      if (elRef.current) {
        if (isEmojiOnly) {
          // Emoji-only: just translate + bob + spin on click. No squash/lean —
          // those make emoji glyphs wobble at small sizes. Real character art
          // (admin-set sprite_url) goes through the image branch below.
          elRef.current.style.transform =
            `translate(${ix}px, ${-iy}px) rotate(${iSpin}deg)`;
        } else {
          // Pokémon Showdown /ani/ front sprites face LEFT by default; flip
          // when moving right so the sprite always faces its direction of motion.
          // (Same applies to admin-set character sprite_url images — uploaders
          // should provide left-facing artwork to match this convention.)
          elRef.current.style.transform =
            `translate(${ix}px, ${-iy}px) scaleX(${-s.facing}) rotate(${iSpin}deg)`;
        }
      }
      if (labelRef.current) {
        labelRef.current.style.transform = `translate(${ix}px, ${-iy}px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, []);

  // Click → bigger jump + 360° spin, then forward to parent onClick (DM open).
  // Fires for own sprite too (no parent onClick), so poking at yourself reacts.
  const handleClick = (e) => {
    const s = stateRef.current;
    if (s && !s.fainted) {
      const now = performance.now();
      s.jumpStart = now;
      s.jumpDur = 520;
      s.jumpPeak = JUMP_HEIGHT * 1.6;
      s.spinStart = now;
      s.spinDur = 520;
    }
    if (onClick) onClick(e);
  };

  return (
    <>
      {renderMode === 'emoji' ? (
        <div
          ref={elRef}
          title={label || slug}
          onClick={handleClick}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: SPRITE_HEIGHT,
            width: SPRITE_HEIGHT,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            fontSize: 44,
            lineHeight: 1,
            // Los sprites caminan pegados al borde inferior, a lo ancho de TODA la
            // pantalla y por encima del contenido (z-index 60). Con pointerEvents:'auto'
            // se quedaban con el clic de cualquier botón que quedara debajo: el usuario
            // apretaba "Enviar" y no pasaba nada, sin error y sin pedido al servidor.
            // Intermitente y no reproducible a voluntad — el peor tipo de bug.
            // Lo encontró Thiago el 2-sep-2026 con una captura donde se ve un Pokémon
            // tapando el botón Enviar del formulario de Ideas y Bugs.
            // Se pierde el atajo de "click en el bicho de un compañero → abre chat con él";
            // vale el cambio: ese atajo es decorativo (el overlay entero es aria-hidden)
            // y el chat se abre igual desde el chat. Un adorno no puede romper botones.
            pointerEvents: 'none',
            userSelect: 'none',
            willChange: 'transform',
            filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.25))',
          }}
        >
          {characterEmoji || CHARACTER_EMOJI[slug] || '👤'}
        </div>
      ) : (
        <img
          ref={elRef}
          src={imageSrc}
          alt={slug}
          title={label || slug}
          draggable={false}
          onClick={handleClick}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: SPRITE_HEIGHT,
            width: 'auto',
            imageRendering: 'pixelated',
            pointerEvents: 'none',  // ver el comentario del sprite emoji, mismo motivo
            userSelect: 'none',
            willChange: 'transform',
            filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.25))',
          }}
        />
      )}
      {/* Own-sprite badge: floats above without a name label */}
      {badge && !label && (
        <div
          ref={labelRef}
          style={{
            position: 'absolute',
            bottom: SPRITE_HEIGHT + 2,
            left: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 10,
            padding: '1px 6px',
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--c-text)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            willChange: 'transform',
            opacity: 0.9,
          }}
          title={`🎫 ${badge.earned} / ${badge.total}`}
        >
          🏆 <span>{badge.earned}/{badge.total}</span>
        </div>
      )}
      {label && (
        <div
          ref={labelRef}
          style={{
            position: 'absolute',
            bottom: SPRITE_HEIGHT + 2,
            left: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            pointerEvents: 'none',
            willChange: 'transform',
          }}
        >
          {Array.isArray(showcase) && showcase.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 2,
                background: 'var(--c-surface)',
                border: '1px solid var(--c-border)',
                borderRadius: 4,
                padding: '1px 4px',
                fontSize: 12,
                lineHeight: 1.1,
                opacity: 0.95,
              }}
              title={showcase.map(s => `${s.emoji || ''} ${s.name}`).join(' · ')}
            >
              {showcase.map((s, i) => (
                <span key={i}>{s.emoji || '🎖️'}</span>
              ))}
            </div>
          )}
          <div style={{
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--c-text-muted)',
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 4,
            padding: '1px 5px',
            whiteSpace: 'nowrap',
            opacity: 0.85,
          }}>
            {label}
          </div>
        </div>
      )}
    </>
  );
});

// Battle tuning
const BATTLE_PROXIMITY_PX = 70;        // distance at which two sprites can clash
const BATTLE_VS_DURATION = 800;        // ms VS flash before result
const BATTLE_RESULT_DURATION = 1500;   // ms winner glow + loser flip animation
const BATTLE_FAINT_DURATION = 9000;    // ms loser stays fainted before respawn
const BATTLE_GLOBAL_COOLDOWN = 6000;   // ms before next battle can start anywhere

export default function PokemonOverlay() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { createConversation, openFloatingConversation } = useChatContext();
  const [roster, setRoster] = useState([]);
  const [opening, setOpening] = useState(false);
  const [battle, setBattle] = useState(null); // { phase: 'vs'|'result', x, winnerLabel?, loserLabel? }

  // Imperative refs to all sprites currently mounted.
  // Map: id (string) → Sprite api { getPosition, isFainted, setPaused, setFainted, respawn }
  const spriteRefs = useRef(new Map());
  // Per-sprite labels for the result banner
  const labelMap = useRef(new Map());
  const battleRef = useRef(null);     // active battle id pair while in progress
  const battleCooldownUntil = useRef(0);

  const isInternal = user && ['admin', 'manager', 'agent'].includes(user.role);
  const killSwitch = !!user?.pokemonGloballyDisabled;
  const ownEnabled = !!(isInternal && !killSwitch && user?.pokemonEnabled !== false && user?.pokemonChoice);
  const showOthers = !!(isInternal && !killSwitch && user?.pokemonShowOthers);

  const openDM = async (otherUserId) => {
    if (!createConversation || !openFloatingConversation) return;
    if (opening) return;
    setOpening(true);
    trackGameEvent('sprite_dm_click', { user_id: otherUserId });
    try {
      const conv = await createConversation('dm', null, null, [otherUserId]);
      if (conv?.id) openFloatingConversation(conv.id);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setOpening(false);
    }
  };

  useEffect(() => {
    if (!showOthers) {
      setRoster([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/auth/pokemon-roster');
        if (!cancelled) setRoster(res.data || []);
      } catch {
        // silent — overlay is non-critical
      }
    };
    load();
    const id = setInterval(load, ROSTER_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [showOthers]);

  // Setter that registers/deregisters a sprite's imperative ref + label.
  const setSpriteRef = (id, label) => (api) => {
    if (api) {
      spriteRefs.current.set(id, api);
      labelMap.current.set(id, label);
    } else {
      spriteRefs.current.delete(id);
      labelMap.current.delete(id);
    }
  };

  // Battle detection — every 400ms scan for two sprites within proximity, facing each other.
  useEffect(() => {
    if (!showOthers || !ownEnabled) return; // need at least 2 sprites
    const id = setInterval(() => {
      if (battleRef.current) return;
      if (Date.now() < battleCooldownUntil.current) return;
      const entries = Array.from(spriteRefs.current.entries());
      if (entries.length < 2) return;
      for (let i = 0; i < entries.length; i++) {
        for (let j = i + 1; j < entries.length; j++) {
          const [idA, apiA] = entries[i];
          const [idB, apiB] = entries[j];
          if (apiA.isFainted() || apiB.isFainted()) continue;
          const pA = apiA.getPosition();
          const pB = apiB.getPosition();
          if (!pA || !pB) continue;
          const dx = pA.x - pB.x; // A relative to B
          if (Math.abs(dx) > BATTLE_PROXIMITY_PX) continue;
          // Approaching: A is right of B and faces left, while B faces right (or mirror).
          const approaching =
            (dx > 0 && pA.facing === -1 && pB.facing === 1) ||
            (dx < 0 && pA.facing === 1 && pB.facing === -1);
          if (!approaching) continue;
          startBattle(idA, idB, apiA, apiB, (pA.x + pB.x) / 2 + 28);
          return;
        }
      }
    }, 400);
    return () => clearInterval(id);
  }, [showOthers, ownEnabled]); // eslint-disable-line

  const startBattle = async (idA, idB, apiA, apiB, midX) => {
    battleRef.current = { idA, idB };
    apiA.setPaused(true);
    apiB.setPaused(true);
    setBattle({ phase: 'vs', x: midX });

    // Ask the server to decide. One of the two clients posts; either way the
    // server dedupes by 30s window so both end up with the same outcome.
    // Local-only (one sprite is 'self', other is a real userId).
    let serverWinnerIsSelf = null;
    if (idA === 'self' || idB === 'self') {
      const opponentId = idA === 'self' ? idB : idA;
      const mySlug = user?.pokemonChoice;
      const oppSlug = roster.find(r => r.id === opponentId)?.pokemon_choice;
      if (mySlug && oppSlug) {
        try {
          const res = await api.post('/battles', {
            opponent_id: opponentId,
            my_slug: mySlug,
            opponent_slug: oppSlug,
          });
          serverWinnerIsSelf = !!res.data?.i_won;
        } catch {
          // fallback to random if server fails
        }
      }
    }

    setTimeout(() => {
      // If server told us, use that; otherwise random
      let winnerIsA;
      if (serverWinnerIsSelf !== null) {
        winnerIsA = (idA === 'self') ? serverWinnerIsSelf : !serverWinnerIsSelf;
      } else {
        winnerIsA = Math.random() < 0.5;
      }
      const winnerApi = winnerIsA ? apiA : apiB;
      const loserApi  = winnerIsA ? apiB : apiA;
      const winnerLabel = labelMap.current.get(winnerIsA ? idA : idB) || '';
      const loserLabel  = labelMap.current.get(winnerIsA ? idB : idA) || '';
      loserApi.setFainted(true);
      setBattle({ phase: 'result', x: midX, winnerLabel, loserLabel });

      setTimeout(() => {
        // Result banner gone, both unpause; loser stays fainted on the ground.
        setBattle(null);
        apiA.setPaused(false);
        apiB.setPaused(false);
      }, BATTLE_RESULT_DURATION);

      setTimeout(() => {
        // Loser respawns at edge, battle slot freed (with cooldown).
        loserApi.setFainted(false);
        loserApi.respawn();
        battleRef.current = null;
        battleCooldownUntil.current = Date.now() + BATTLE_GLOBAL_COOLDOWN;
      }, BATTLE_FAINT_DURATION);
    }, BATTLE_VS_DURATION);
  };

  if (!ownEnabled && !showOthers) return null;

  // Hide on small viewports — picker still works on mobile but the floor gets crowded.
  if (typeof window !== 'undefined' && window.innerWidth < 640) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        height: OVERLAY_HEIGHT,
        pointerEvents: 'none',
        zIndex: 60,
        overflow: 'hidden',
      }}
    >
      {ownEnabled && (
        <Sprite
          ref={setSpriteRef('self', t('pokemonOverlay.you'))}
          slug={user.pokemonChoice}
          spriteUrl={user.pokemonSpriteUrl || null}
          label={null}
          badge={user.stickerTotal > 0 ? { earned: user.stickerCount || 0, total: user.stickerTotal } : null}
        />
      )}
      {showOthers && roster.map((r) => (
        <Sprite
          key={r.id}
          ref={setSpriteRef(r.id, r.name)}
          slug={r.pokemon_choice}
          spriteUrl={r.character_sprite_url || null}
          label={r.name}
          showcase={r.showcase}
          characterEmoji={r.character_emoji}
          onClick={() => openDM(r.id)}
        />
      ))}
      {battle && <BattleOverlay battle={battle} t={t} />}
    </div>
  );
}

// Centered VS / result banner. Anchored at battle.x in the bottom strip.
function BattleOverlay({ battle, t }) {
  if (battle.phase === 'vs') {
    return (
      <div
        className="sprite-battle-vs"
        style={{
          position: 'absolute',
          left: battle.x,
          bottom: SPRITE_HEIGHT + 18,
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
        }}
      >
        <span className="sprite-battle-vs-text">⚡ VS ⚡</span>
      </div>
    );
  }
  return (
    <div
      className="sprite-battle-result"
      style={{
        position: 'absolute',
        left: battle.x,
        bottom: SPRITE_HEIGHT + 14,
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
        textAlign: 'center',
      }}
    >
      <div className="sprite-battle-trophy">🏆</div>
      <div className="sprite-battle-winner">
        {t('pokemonOverlay.battleWinner', { name: battle.winnerLabel })}
      </div>
    </div>
  );
}
